import { useContext, useState, useMemo, useEffect } from "react";
import {
    Alert,
    AlertTitle,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import SelectLojaComPesquisa from "../../../../../../components/SelectsSimpleApiQuery/SelectLojaComPesquisa";
import SelectCondicoesPagamento from "../../../../../../components/SelectsSimpleApiQuery/SelectCondicoesPagamento";
import { AuthContext } from "../../../../../../contexts/AuthContext";
import FormasPagamento from "./FormasPagamento";
import { TCheckoutForm } from "../../../MIPedidos/DialogCriarPedidoVenda/CriarPedido/Checkout";
import { DadoValorPrinter } from "../../../../../../components/BasicComponents/DisplayDados/DadoValorPrinter";
import {
    formatarMonetárioParaNúmero,
    formatarValorMonetario,
} from "../../../../../../helpers/masks";
import {
    IInternFormCheckout
} from "../../../MIPedidos/DialogCriarPedidoVenda/CriarPedido/Checkout/FormasPagamento/DialogCadastrarValor";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { ICarrinhoItem } from "../../../MIPedidos/DialogCriarPedidoVenda/CriarPedido";
import { IDataPublicos, TFazendaPublico } from "../../../../../../types/api/geral/pesquisar-publicos";
import moment from "moment";
import { IEditarPagamentoPedidoPayload } from "../../../../../../types/mutations/comercial/editar-pedido";
import PaperDraggable from "../../../../../../components/PaperDraggable";

export default function EditarPagamento({ open, onClose }: PropsEditarPedido) {

    const { setValue, watch, control } = useFormContext();
    const queryClient = useQueryClient();
    const { getUserData } = useContext(AuthContext);
    const userData = getUserData();
    const [defaultValue, setDefaultValue] = useState(true);
    const data = watch() as TEditForm;

    //Querys e mutations
    const { mutate: editarPagamentoPedido, isLoading } = useMutation({
        mutationKey: "editar-pagamento-pedido",
        mutationFn: (data: IEditarPagamentoPedidoPayload) =>
            axiosClient.put(`/comercial/pedidos/editar-pagamento-pedido/${data.id}`, data),
        onError: (error) => {
            console.error(error);
            enqueueSnackbar("Erro ao editar pedido!", { variant: "error" });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(`comercial/detalhar-pedido`);
            onClose();
            enqueueSnackbar("Pedido editado com sucesso!", { variant: "success" });
        },
    })

    // valores importantes
    const valoresAdicionados: IInternFormCheckout[] = watch('valoresAdicionados');
    const valorFrete = Number(watch("valor_frete"));
    const subTotal = watch("sub_total");
    const acrescimos = watch("valor_acrescimo");

    const submit = () => {
        const formdata: IEditarPagamentoPedidoPayload = {
            id: data.id,
            valoresAdicionados: data.valoresAdicionados.map(
                (item: IInternFormCheckout) => {
                    return {
                        status: item.status,
                        valor: item.valor,
                        forma_recebimento: item.forma_recebimento,
                        pagamento_id: Number(item.pagamento_id) ?? 0,
                        pagamento: item.pagamento.parcelas.length > 0
                            ? item.pagamento :
                            {

                                ...item.pagamento,
                                parcelas: [
                                    {
                                        valor: Number(
                                            formatarMonetárioParaNúmero(item.valor)
                                        ),
                                        vencimento: moment().format("DD/MM/YYYY"),
                                    },
                                ],

                            }
                    }
                }
            )
        }
        editarPagamentoPedido(formdata);
    }

    const todosIdsBloqueados = userData?.perfis_vinculados.flatMap(
        perfil => perfil.condicoes_pagamento_blocked ?? []
    );

    const valorRestante = useMemo(() => {
        const subTotalNormalizado = normalizarValor(String(subTotal));
        const valorFreteNormalizado = normalizarValor(String(valorFrete));
        const valorDesconto = data.descontos || 0;
        
        const totalAtt = Number((subTotalNormalizado - valorDesconto + valorFreteNormalizado).toFixed(2));

        let totalValores = 0;
        if (valoresAdicionados?.length) {
            totalValores = valoresAdicionados.reduce((acc: number, item: any) => {
                const valorItem = normalizarValor(String(item.valor));
                return acc + valorItem;
            }, 0);

            totalValores = Number(totalValores.toFixed(2));
        }

        return totalAtt - totalValores;
    }, [subTotal, valoresAdicionados, valorFrete, data.descontos]);


    useEffect(() => {
        setValue("valor_restante", valorRestante);
    }, [valorRestante, setValue]);

    useEffect(() => {
        const novoParcelamentoMaximo = data.itens.length > 0
            ? Math.min(...data.itens.map(item => item.setor?.maximo_parcelas ?? 0))
            : 0;
        setValue("parcelamento_maximo", novoParcelamentoMaximo);
    }, [data.itens, setValue]);

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="sm"
            PaperComponent={PaperDraggable}
            aria-labelledby="draggable-dialog-title"
            scroll="body"
        >
            <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
                Alterar pagamento
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} mb={1}>
                    <Grid size={{xs: 12}} mt={1}>
                        <Controller
                            control={control}
                            name="loja_faturamento"
                            rules={{ required: "Campo obrigatório" }}
                            render={({ field }) => (
                                <SelectLojaComPesquisa
                                    disabled
                                    label="Loja de origem"
                                    value={field.value}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12}}>
                        <Controller
                            control={control}
                            name="condicao_pagamento"
                            rules={{ required: "Campo obrigatório" }}
                            render={({ field }) => (
                                <>
                                    <SelectCondicoesPagamento
                                        defaultValue={defaultValue}
                                        tipo_publico={data.publico.tipo_publico}
                                        codigo_publico={data.publico.codigo_publico}
                                        situacao_financeira={data.publico.situacao_financeira}
                                        produtor_rural={!!data.publico.selected_fazenda}
                                        desabilitarInput={true}
                                        loja_id={watch("loja_faturamento")?.id || null}
                                        autoSelectFirst
                                        condicoes_pagamento_blocked={todosIdsBloqueados || null}
                                        valor_total={data.sub_total + Number(data.valor_frete)}
                                        status={data?.condicao_pagamento?.status as "Ativa" | "Inativa" | undefined}
                                        value={field.value}
                                        onChange={e => {
                                            if (defaultValue) field.onChange(e);
                                            setDefaultValue(false);
                                        }}
                                    />

                                    {field.value && field.value.alertas.length > 0 && (
                                        <Alert severity="warning" icon={false} sx={{ mt: 2 }}>
                                            <AlertTitle>Atenção:</AlertTitle>
                                            {field.value.alertas.map((item: string, idx: number) => (
                                                <li key={idx}>{item}</li>
                                            ))}
                                        </Alert>
                                    )}
                                </>
                            )}
                        />
                    </Grid>
                    <Grid size={{xs: 12}}>
                        <Stack direction="row" justifyContent="space-around">
                            <DadoValorPrinter
                                alignValue="right"
                                alignTitle="right"
                                titulo="Subtotal"
                                valor={formatarValorMonetario(data.sub_total)}
                            />
                            <DadoValorPrinter
                                alignValue="right"
                                titulo="Acréscimos"
                                valor={formatarValorMonetario(acrescimos)}
                            />
                            <DadoValorPrinter
                                alignValue="right"
                                alignTitle="right"
                                titulo="Descontos"
                                valor={formatarValorMonetario(data.descontos)}
                            />
                            <DadoValorPrinter
                                alignValue="right"
                                alignTitle="right"
                                titulo="Total"
                                valor={formatarValorMonetario(data.sub_total + Number(data.valor_frete) + acrescimos - data.descontos)}
                            />
                        </Stack>
                    </Grid>
                    <Grid size={{xs: 12}}>
                        <FormasPagamento cliente={data.publico} subTotal={data.total} />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    type="submit"
                    form="editar-pagamento-form"
                    onClick={submit}
                    disabled={isLoading || valorRestante !== 0}
                >
                    {isLoading ? "Editando..." : "Editar"}
                </Button>

            </DialogActions>
        </Dialog>
    );
}

const normalizarValor = (valor: string | number): number => {
    if (typeof valor === "number") return valor;
    const valorLimpo = valor.trim();
    if (valorLimpo.includes(",")) {
        return parseFloat(valorLimpo.replace(/\./g, '').replace(',', '.'));
    }
    return parseFloat(valorLimpo);
};

interface PropsEditarPedido {
    open: boolean;
    onClose: () => void;
}

export type TEditForm = TCheckoutForm & {
    id: number;
    situacao: string;
    codigo: string;
    publico: IDataPublicos;
    valor_desconto: number;
    itens: ICarrinhoItem[];
    selected_fazenda: TFazendaPublico | null;
    descontos: number;
    total: number;
    acrescimos: number;
    sub_total: number;
};